/**
 * Scroll-Triggered Popup
 * Shows when user scrolls 50-70% down the page (engaged with content)
 */

import React, { useState, useEffect } from 'react';
import BasePopup from './BasePopup';
import PopupForm from './PopupForm';

export interface ScrollPopupProps {
  enabled?: boolean;
  scrollPercentage?: number; // Show after this % of page scroll (default: 60%)
  delay?: number; // Additional delay after scroll threshold (ms)
}

const ScrollPopup: React.FC<ScrollPopupProps> = ({
  enabled = true,
  scrollPercentage = 60,
  delay = 1000,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    // Only stop showing if user has actually signed up
    const hasSignedUp = localStorage.getItem('landListingSignup');
    if (hasSignedUp) {
      setHasShown(true);
      return;
    }

    const handleScroll = () => {
      if (hasShown) return;

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      const scrolled = (scrollTop / (documentHeight - windowHeight)) * 100;

      if (scrolled >= scrollPercentage) {
        // Add delay before showing
        setTimeout(() => {
          setIsOpen(true);
          setHasShown(true);
        }, delay);

        // Remove listener after triggering
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, scrollPercentage, delay, hasShown]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSuccess = () => {
    setIsOpen(false);
    // Mark that user has signed up - don't show popup again
    localStorage.setItem('landListingSignup', 'true');
    alert('Success! Check your email for the free checklist.');
  };

  if (!enabled) return null;

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Get Weekly Land Opportunities"
      subtitle="Delivered to your inbox"
    >
      <PopupForm
        source="scroll_popup"
        onSuccess={handleSuccess}
        buttonText="Get Weekly Land Lots"
      />
    </BasePopup>
  );
};

export default ScrollPopup;
